import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ResponseData } from '@freelancer/datastore';
import { LoginError } from '@freelancer/login-signup';
import { Pwa } from '@freelancer/pwa';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { Focus } from '@freelancer/ui/focus';
import { HeadingType } from '@freelancer/ui/heading';
import { InputComponent, InputSize, InputType } from '@freelancer/ui/input';
import { LogoSize } from '@freelancer/ui/logo';
import { Margin } from '@freelancer/ui/margin';
import { PictureDisplay } from '@freelancer/ui/picture';
import { FontType, TextAlign, TextSize } from '@freelancer/ui/text';
import { dirtyAndValidate } from '@freelancer/ui/validators';
import { ErrorCodeApi } from 'api-typings/errors/errors';

@Component({
  selector: 'app-credentials-form',
  template: `
    <app-form-logo
      [size]="logoSize"
      [flMarginBottom]="Margin.MID"
    ></app-form-logo>
    <form class="LoginForm" [formGroup]="formGroup" (ngSubmit)="handleLogin()">
      <fl-heading
        class="LoginForm-heading"
        i18n="Login form heading"
        [headingType]="HeadingType.H2"
        [size]="TextSize.MID"
        [flMarginBottom]="Margin.LARGE"
      >
        Welcome Back
      </fl-heading>
      <app-facebook-button
        i18n="Login form Facebook login button"
        [disabled]="!facebookSdkLoaded"
        [flMarginBottom]="Margin.MID"
        [stepLabel]="'CredentialsForm'"
        (click)="handleFacebookClick()"
      >
        Log In With Facebook
      </app-facebook-button>
      <fl-hr
        i18n-label="Login form divider"
        label="or"
        [flMarginBottom]="Margin.MID"
      ></fl-hr>
      <fl-input
        flTrackingLabel="CredentialsForm-EmailInput"
        placeholder="Email or Username"
        i18n-placeholder="Login form email/username field placeholder"
        [flMarginBottom]="Margin.SMALL"
        [control]="formGroup.get('user')"
        [size]="InputSize.LARGE"
        [type]="InputType.EMAIL"
        #focusInput
      ></fl-input>
      <fl-input
        flTrackingLabel="CredentialsForm-PasswordInput"
        placeholder="Password"
        i18n-placeholder="Login form password field placeholder"
        [flMarginBottom]="Margin.MID"
        [control]="formGroup.get('password')"
        [size]="InputSize.LARGE"
        [type]="InputType.PASSWORD"
      ></fl-input>
      <fl-bit class="LoginForm-rememberMe" [flMarginBottom]="Margin.XXSMALL">
        <fl-checkbox
          [flPwaHideInstalled]="true"
          flTrackingLabel="CredentialsForm-RememberMe"
          i18n-label="Login form remember me checkbox label"
          label="Remember me"
          [control]="formGroup.get('rememberMe')"
        ></fl-checkbox>
        <fl-link
          flTrackingLabel="CredentialsForm-ForgotPassword"
          i18n="Login page forgot password link"
          (click)="resetPassword.emit()"
        >
          Forgot Password?
        </fl-link>
      </fl-bit>
      <ng-container *ngIf="response">
        <fl-banner-alert
          *ngIf="response.status === 'error'"
          [type]="BannerAlertType.ERROR"
          [ngSwitch]="response.errorCode"
          [flMarginBottom]="Margin.SMALL"
        >
          <ng-container
            *ngSwitchCase="ErrorCodeApi.AUTH_USER_MISSING"
            i18n="Login wrong username password message"
          >
            Incorrect username or password.
          </ng-container>
          <ng-container
            *ngSwitchCase="ErrorCodeApi.AUTH_ACCOUNT_CLOSED"
            i18n="Login wrong username password message"
          >
            This account has been closed. Please contact
            <fl-link
              flTrackingLabel="CredentialsForm-LoginClosedAccountSupportLink"
              [link]="'/support'"
              >support</fl-link
            >
            for further details.
          </ng-container>
          <!-- this shouldn't actually appear: see login-component::112 -->
          <ng-container
            *ngSwitchCase="ErrorCodeApi.AUTH_ACCOUNT_SUSPENDED"
            i18n="Login wrong username password message"
          >
            This account has been suspended. Please visit
            <fl-link
              flTrackingLabel="CredentialsForm-LoginSuspendedAccountLink"
              [link]="'/users/usersuspended'"
              >this page</fl-link
            >
            for further details.
          </ng-container>
          <ng-container
            *ngSwitchCase="ErrorCodeApi.AUTH_CAPTCHA_REQUIRED"
            i18n="Login wrong username password message"
          >
            Please complete the CAPTCHA to log in.
          </ng-container>
          <ng-container
            *ngSwitchCase="ErrorCodeApi.AUTH_2FA_BLOCKED"
            i18n="Login wrong username password message"
          >
            Your login has been blocked due to signing in from an unknown
            device. Check your email and enter the code to log in.
          </ng-container>
          <ng-container
            *ngSwitchCase="ErrorCodeApi.AUTH_FACEBOOK_LINK_FAILED"
            i18n="Login facebook failed message"
          >
            Could not connect your Facebook account. Please try again.
          </ng-container>
          <ng-container
            *ngSwitchCase="ErrorCodeApi.AUTH_FACEBOOK_EMAIL_EXISTS"
            i18n="Login facebook failed message"
          >
            <!-- "again" because this error only shows up if the user goes back from FB link -->
            Your email address is already associated with a Freelancer account.
            Please log in with Facebook again and link your accounts.
          </ng-container>
          <ng-container
            *ngSwitchCase="ErrorCodeApi.AUTH_DEVICE_TOKEN_INVALID"
            i18n="Login unrecoverable error"
          >
            Something went wrong. Please refresh the page and try again, or
            contact
            <fl-link
              flTrackingLabel="CredentialsForm-LoginErrorSupportLink"
              [link]="'/support'"
              >support</fl-link
            ><fl-text *ngIf="response.requestId" [fontType]="FontType.SPAN">
              with request ID {{ response.requestId }}</fl-text
            >.
          </ng-container>
          <ng-container *ngSwitchDefault i18n="Login unknown error message">
            Something went wrong. Please check your credentials and try again,
            or contact
            <fl-link
              flTrackingLabel="CredentialsForm-LoginErrorSupportLink"
              [link]="'/support'"
              >support</fl-link
            ><fl-text *ngIf="response.requestId" [fontType]="FontType.SPAN">
              with request ID {{ response.requestId }}</fl-text
            >.
          </ng-container>
        </fl-banner-alert>
      </ng-container>
      <app-login-signup-button
        i18n="Login form button (this is an action)"
        [busy]="response === null"
        [flMarginBottom]="Margin.LARGE"
        [stepLabel]="'CredentialsForm'"
      >
        Log In
      </app-login-signup-button>
      <fl-hr [flMarginBottom]="Margin.SMALL"></fl-hr>
      <fl-text
        i18n="Login form link to signup page"
        [textAlign]="TextAlign.CENTER"
        [size]="TextSize.XXSMALL"
        [flMarginBottom]="Margin.MID"
      >
        Don't have an account?
        <fl-link
          flTrackingLabel="CredentialsForm-SignupLink"
          [size]="TextSize.XXSMALL"
          (click)="switchToSignup.emit()"
        >
          Sign Up
        </fl-link>
      </fl-text>
      <fl-bit class="LoginMobileSection" [flPwaHideInstalled]="true">
        <fl-link
          [link]="'https://bnc.lt/Ed5l/nPQu9RdcWr'"
          [flMarginRight]="Margin.SMALL"
          [flTrackingLabel]="'CredentialsForm-DownloadAppStore'"
        >
          <fl-picture
            class="LoginMobileSection-badge"
            alt="Apple app store logo"
            i18n-alt="Footer App Install logo alt text"
            [src]="'login-signup/app-store-badges/apple-app-store-badge.png'"
            [display]="PictureDisplay.BLOCK"
          ></fl-picture>
        </fl-link>
        <fl-link
          [link]="'https://bnc.lt/Ed5l/nPQu9RdcWr'"
          [flTrackingLabel]="'CredentialsForm-DownloadPlayStore'"
        >
          <fl-picture
            class="LoginMobileSection-badge"
            alt="Google Play logo"
            i18n-alt="Footer App Install logo alt text"
            [src]="'login-signup/app-store-badges/google-play-store-badge.png'"
            [display]="PictureDisplay.BLOCK"
          ></fl-picture>
        </fl-link>
      </fl-bit>
    </form>
  `,
  styleUrls: ['./credentials-form.component.scss'],
})
export class CredentialsFormComponent implements OnInit, AfterViewInit {
  BannerAlertType = BannerAlertType;
  TextSize = TextSize;
  FontType = FontType;
  HeadingType = HeadingType;
  InputSize = InputSize;
  InputType = InputType;
  LogoSize = LogoSize;
  Margin = Margin;
  PictureDisplay = PictureDisplay;
  TextAlign = TextAlign;

  ErrorCodeApi = ErrorCodeApi;

  @Input() logoSize = LogoSize.MID;
  @Input() facebookSdkLoaded = false;
  @Input() formGroup: FormGroup;
  @Input() response?: ResponseData<unknown, LoginError> | null;

  @Output() complete = new EventEmitter();
  @Output() resetPassword = new EventEmitter();
  @Output() facebookClick = new EventEmitter();
  @Output() switchToSignup = new EventEmitter();

  @ViewChild('focusInput', { static: true }) focusInput: InputComponent;

  constructor(private focus: Focus, private pwa: Pwa) {}

  ngOnInit() {
    if (this.pwa.isInstalled()) {
      const rememberMe = this.formGroup.get('rememberMe');
      if (rememberMe) {
        rememberMe.setValue(true);
      }
    }
  }

  ngAfterViewInit() {
    this.focus.focusElement(this.focusInput.nativeElement);
  }

  handleLogin() {
    if (this.response === null) {
      return;
    }
    dirtyAndValidate(this.formGroup);
    if (this.formGroup.valid) {
      this.complete.emit();
    }
  }

  handleFacebookClick() {
    this.facebookClick.emit();
  }
}
